import {
    ReactNode,
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState
} from 'react';
import type { ResourceEntryKind } from '../utils/resourceIndex';

/** Open state + global ⌘K binding for the search layer, so pages don't each register a listener. */
interface CommandPaletteContextValue {
    isOpen: boolean;
    /** Seed query, set when a page opens the palette with a suggestion. */
    initialQuery: string;
    /** Page-declared scope the palette opens on; null = "Everything" (see useSearchScope). */
    searchScope: ResourceEntryKind | null;
    setSearchScope: (kind: ResourceEntryKind | null) => void;
    open: (query?: string) => void;
    close: () => void;
}

const CommandPaletteContext = createContext<CommandPaletteContextValue | undefined>(undefined);

function isTypingTarget(target: EventTarget | null): boolean {
    if (!(target instanceof HTMLElement)) return false;
    return (
        target.isContentEditable ||
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.tagName === 'SELECT'
    );
}

export function CommandPaletteProvider({ children }: { children: ReactNode }) {
    const [isOpen, setIsOpen] = useState(false);
    const [initialQuery, setInitialQuery] = useState('');
    const [searchScope, setSearchScope] = useState<ResourceEntryKind | null>(null);

    const open = useCallback((query: string = '') => {
        setInitialQuery(query);
        setIsOpen(true);
    }, []);

    const close = useCallback(() => setIsOpen(false), []);

    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
                event.preventDefault();
                setInitialQuery('');
                setIsOpen((previous) => !previous);
                return;
            }
            // `/` is a shortcut only when it is not being typed into a field.
            if (event.key === '/' && !isTypingTarget(event.target)) {
                event.preventDefault();
                setInitialQuery('');
                setIsOpen(true);
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, []);

    const value = useMemo(
        () => ({ isOpen, initialQuery, searchScope, setSearchScope, open, close }),
        [isOpen, initialQuery, searchScope, open, close]
    );

    return (
        <CommandPaletteContext.Provider value={value}>{children}</CommandPaletteContext.Provider>
    );
}

/** Scopes the palette to `kind` while the calling page is mounted; cleared on unmount. */
export function useSearchScope(kind: ResourceEntryKind): void {
    const { setSearchScope } = useCommandPalette();

    useEffect(() => {
        setSearchScope(kind);
        return () => setSearchScope(null);
    }, [kind, setSearchScope]);
}

export function useCommandPalette(): CommandPaletteContextValue {
    const context = useContext(CommandPaletteContext);
    if (context === undefined) {
        throw new Error('useCommandPalette must be used within a CommandPaletteProvider');
    }
    return context;
}
